import React, { useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { Container, Eyebrow, Display, Lead, Arrow, useReveal } from "./ui";
import { PilotResults } from "./Interactive";
import { colors, fonts, media } from "../../styles/theme";
import { proof } from "../../pages/landingContent";
import { pilotResults } from "../../pages/verticalContent";

// Evidence without individuals: pilot data plus the visible signs of calm.

const Wrap = styled.section`
  padding: 140px 0;
  background: linear-gradient(180deg, ${colors.limestone} 0%, ${colors.linen} 100%);
`;

const Head = styled.div`
  display: grid;
  gap: 24px;
  margin-bottom: 48px;

  @media ${media.medium} {
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    align-items: end;
  }
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  align-items: stretch;

  @media ${media.large} {
    grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
  }
`;

const Signs = styled.div`
  display: flex;
  flex-direction: column;
  padding: 36px 28px;
  border-radius: 28px;
  background: ${colors.forest};
  color: ${colors.linen};

  @media ${media.medium} {
    padding: 44px;
  }

  h3 {
    font-family: ${fonts.display};
    font-weight: 300;
    font-size: clamp(28px, 3vw, 40px);
    letter-spacing: -0.03em;
  }
  ol {
    list-style: none;
    counter-reset: sign;
    margin: 28px 0;
    display: grid;
    gap: 4px;
  }
  li {
    counter-increment: sign;
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 0;
    border-bottom: 1px solid rgba(250, 247, 241, 0.12);
    font-family: ${fonts.display};
    font-size: 22px;
    letter-spacing: -0.015em;
  }
  li::before {
    content: counter(sign, decimal-leading-zero);
    font-family: ${fonts.mono};
    font-size: 12px;
    color: ${colors.copperLight};
  }
  p {
    margin-top: auto;
    font-family: ${fonts.body};
    font-size: 16px;
    line-height: 1.55;
    color: rgba(250, 247, 241, 0.72);
  }
`;

const Observed = styled.div`
  margin-top: 20px;
  padding: 28px;
  border-radius: 28px;
  background: ${colors.sand};

  h3 {
    font-family: ${fonts.mono};
    font-size: 12px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.bark};
  }
  ul {
    list-style: none;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 16px;
  }
  li {
    padding: 10px 16px;
    border-radius: 999px;
    background: ${colors.linen};
    border: 1px solid ${colors.stone};
    font-family: ${fonts.body};
    font-size: 15px;
    font-weight: 500;
    color: ${colors.ink};
  }
`;

const More = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
  font-family: ${fonts.body};
  font-size: 16px;
  font-weight: 600;
  color: ${colors.copperLight};

  svg {
    transition: transform 0.3s ease;
  }
  &:hover svg {
    transform: translateX(4px);
  }
`;

const Proof = () => {
  const scope = useRef(null);
  useReveal(scope);
  return (
    <Wrap id="proof" ref={scope} aria-labelledby="proof-title">
      <Container>
        <Head>
          <div data-reveal>
            <Eyebrow $color={colors.terracotta}>{proof.eyebrow}</Eyebrow>
            <Display id="proof-title" style={{ marginTop: 20 }}>
              {proof.titleLead} <em>{proof.titleEmphasis}</em>
            </Display>
          </div>
          <Lead data-reveal="0.1">{proof.body}</Lead>
        </Head>
        <Grid>
          <div data-reveal>
            <PilotResults data={pilotResults} accent={colors.moss} />
          </div>
          <Signs data-reveal="0.1">
            <h3>{proof.signsTitle}</h3>
            <ol>
              {proof.signs.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            <p>{proof.signsNote}</p>
            <More to="/workplace-safety">
              See the workplace results <Arrow />
            </More>
          </Signs>
        </Grid>
        <Observed data-reveal>
          <h3>{proof.observationsTitle}</h3>
          <ul>
            {proof.observations.map((o) => (
              <li key={o}>{o}</li>
            ))}
          </ul>
        </Observed>
      </Container>
    </Wrap>
  );
};

export default Proof;
